import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useSubject } from '../context/SubjectContext'
import { supabase } from '../lib/supabase'
import { fetchMyFirmName } from '../lib/partnerLookup'
import { captureReferralIfPresent } from '../lib/partnerAttribution'
import Wordmark from './Wordmark'
import { isIntakeRouteEnabled } from '../lib/intake/intakeFlag'

// ── Nav items ──────────────────────────────────────────────────────────────────

const consumerNavItems = [
  // { to: '/profile',    label: 'Profile' },
  // { to: '/chat',       label: 'Chat' },
  { to: '/intelligence', label: 'Tax Intelligence Center' },
  // { to: '/actions',  label: 'Actions' },
  // { to: '/subjects',   label: 'Subjects' },
]

const advisorNavItems = [
  { to: '/advisor', label: 'Pipeline' },
  // Ticket 073: an advisor's own clients. Only while the intake browser flag is on.
  ...(isIntakeRouteEnabled(process.env.REACT_APP_INTAKE_ENABLED) ? [{ to: '/advisor/clients', label: 'My clients' }] : []),
]

// ── Sub-components ─────────────────────────────────────────────────────────────

function HeaderLink({ to, children }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => `hs-nav-link${isActive ? ' active' : ''}`}
    >
      {children}
    </NavLink>
  )
}

function SubjectSwitcher() {
  const { subjects, activeSubject, setActiveSubject } = useSubject()
  const [open, setOpen] = useState(false)

  if (subjects.length <= 1) return null

  return (
    <div style={{ position: 'relative' }}>
      <button className="hs-switcher-btn" onClick={() => setOpen(o => !o)}>
        <span className="hs-switcher-dot" />
        <span className="hs-switcher-name">{activeSubject?.display_name}</span>
        <svg className="hs-switcher-chevron" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <>
          <div className="hs-switcher-backdrop" onClick={() => setOpen(false)} />
          <div className="hs-switcher-dropdown">
            <div className="hs-switcher-label">Active subject</div>
            {subjects.map(s => (
              <button
                key={s.id}
                className={`hs-switcher-item${s.id === activeSubject?.id ? ' active' : ''}`}
                onClick={() => { setActiveSubject(s); setOpen(false) }}
              >
                <span className="hs-switcher-item-dot" />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.display_name}
                </span>
                {s.subject_type === 'self' && (
                  <span className="hs-switcher-item-you">you</span>
                )}
              </button>
            ))}
            <div className="hs-switcher-footer">
              <Link to="/subjects" onClick={() => setOpen(false)}>Manage subjects →</Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ── AppShell ───────────────────────────────────────────────────────────────────

export default function AppShell({ children }) {
  const { user, signOut } = useAuth()
  const location = useLocation()

  const isPaid = false
  const messagesRemainingToday = null

  // Seed from sessionStorage to avoid consumer-nav flash on navigation.
  // The async fetch confirms/updates the value on first load.
  const [userType,        setUserType]        = useState(
    () => sessionStorage.getItem('hs_user_type') || null
  )
  const [firmName,        setFirmName]        = useState(null)
  const [partnerMenuOpen,  setPartnerMenuOpen]  = useState(false)
  const [consumerMenuOpen, setConsumerMenuOpen] = useState(false)
  const partnerMenuRef  = useRef(null)
  const consumerMenuRef = useRef(null)

  // Capture ?ref= into a persistent cookie, once, as early in the app as
  // possible — AppShell wraps every route (see the showChrome/structural-
  // stability comment further down), so this is the one place guaranteed
  // to run regardless of which page a partner link lands on. First-touch:
  // captureReferralIfPresent() is a no-op if a referral was already
  // captured earlier in this browser (see src/lib/partnerAttribution.js).
  // This replaces three independent, per-page `location.search` reads that
  // used to live in CalculatorPage.js, AutonomoCalculatorPage.js, and
  // useTriageForm.js — none of which survived navigating to a page whose
  // URL no longer had ?ref= in it.
  useEffect(() => {
    captureReferralIfPresent()
  }, [])

  // Load user type + firm name once on mount
  useEffect(() => {
    if (!user) return
    supabase
      .from('profiles')
      .select('user_type, partner_id')
      .eq('id', user.id)
      .single()
      .then(async ({ data: profile }) => {
        const type = profile?.user_type || 'consumer'
        setUserType(type)
        sessionStorage.setItem('hs_user_type', type)
        if (type === 'partner' && profile?.partner_id) {
          const { data: firmName } = await fetchMyFirmName()
          setFirmName(firmName || null)
        }
      })
  }, [user?.id])  // stable: only re-runs if the actual user changes, not on token refresh

  // Close partner dropdown on outside click
  useEffect(() => {
    if (!partnerMenuOpen) return
    function handler(e) {
      if (partnerMenuRef.current && !partnerMenuRef.current.contains(e.target)) {
        setPartnerMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [partnerMenuOpen])

  // Close consumer dropdown on outside click
  useEffect(() => {
    if (!consumerMenuOpen) return
    function handler(e) {
      if (consumerMenuRef.current && !consumerMenuRef.current.contains(e.target)) {
        setConsumerMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [consumerMenuOpen])

  async function handleSignOut() {
    await signOut()
    sessionStorage.removeItem('hs_user_type')
    // Also clear the active subject/tax-year selection (set by
    // useSubjects.js) — otherwise the next person to sign in on this
    // browser could briefly see the previous user's subject/tax-year
    // selection persisted in sessionStorage before their own data loads.
    sessionStorage.removeItem('active_subject_id')
    sessionStorage.removeItem('active_tax_year')
    // Hard navigation rather than react-router's navigate(): sign-out
    // needs user, activeSubject, taxProfile, etc. fully cleared from
    // memory across the whole tree, not just wherever the current page
    // happens to look. Also guarantees you land on portal home regardless
    // of what page you signed out from — previously this function never
    // navigated at all, so signing out from a public token-based result
    // page (which doesn't require auth to view) just left you there.
    // Portal home is '/' (see App.js's HomeRedirect/StartRoute/
    // ProtectedRoute/PartnerRoute) — was /calculator, changed to match the
    // new start-page front door (portal_start_page_plan.md).
    window.location.href = '/'
  }

  const isPartner = userType === 'partner'
  // While userType is still loading, show no nav links to avoid flash
  const navItems  = userType === null ? [] : isPartner ? advisorNavItems : consumerNavItems
  // Consumer wordmark click target is now '/' (the start page), not
  // /intelligence — /intelligence is the workspace now, not portal home.
  // See portal_start_page_plan.md.
  const homeLink  = isPartner ? '/advisor' : '/'

  // FIXED: was location.pathname === '/start' — the route was renamed to
  // /spainsnapshot a while ago (see App.js's legacy /start redirect), but
  // this check never got updated, which is why the "Triage intake" pill
  // never actually showed (flagged in session_recap_cta_testing.md's
  // backlog, not fixed until now).
  const onTriage     = location.pathname === '/spainsnapshot'
  // Tool pages: triage flow + both calculator journeys — use minimal
  // shell layout. FIXED alongside onTriage above: startsWith('/calculator')
  // never matched '/autonomo-calculator' (different string prefix), and
  // '/start/results' never matched the renamed '/spainsnapshot/results'
  // either — both silently fell through to the full-nav layout. Rewritten
  // as one prefix check per real route family instead of three
  // independent, driftable checks.
  const onToolPage   = location.pathname.startsWith('/spainsnapshot')
    || location.pathname.startsWith('/calculator')
    || location.pathname.startsWith('/autonomo-calculator')
  const onAuth       = location.pathname === '/auth' || location.pathname === '/reset-password'

  if (onAuth) return <>{children}</>

  // Anonymous users get the same "no visible chrome" look they've always
  // had on tool/result pages — but that now has to be done by hiding the
  // header/footer's CONTENT via CSS, not by omitting the elements
  // themselves. AuthAwareRoute (App.js) now always wraps children in
  // AppShell regardless of auth state, specifically so children's DOM
  // ancestor chain never changes shape when `user` flips from null to a
  // real object mid-session (that's what was silently wiping out
  // CalculatorPage's in-progress result with no navigation at all). If
  // this component conditionally omitted the <header>/<footer> elements
  // instead of just hiding them, that would reintroduce the exact same
  // problem one level down: with no explicit `key`, React matches
  // unkeyed siblings by index, so removing/adding a sibling ahead of the
  // div wrapping {children} shifts everything after it by one slot and
  // forces a remount of {children} anyway. Header and footer must always
  // render, in the same order, every single render — only their
  // visibility (and inner content) can vary.
  const showChrome = !!user

  return (
    <div className="hs-shell">
      <header className={`hs-header${showChrome ? '' : ' hs-header--hidden'}`}>
        <div className="hs-header-inner">

          {/* Left: wordmark + nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', minWidth: 0 }}>
            <Link to={homeLink} className="hs-wordmark">
              <div className="u-wordmark-on-dark"><Wordmark /></div>
            </Link>

            <nav className="hs-nav">
              {navItems.map(item => (
                <HeaderLink key={item.to} to={item.to}>{item.label}</HeaderLink>
              ))}
            </nav>
            {onTriage && <span className="hs-triage-pill">Triage intake</span>}
          </div>

          {/* Right: controls — partner vs consumer */}
          <div className="hs-controls">
            {isPartner ? (
              // ── Partner controls ──────────────────────────────────────────
              <div ref={partnerMenuRef} style={{ position: 'relative' }}>
                <button
                  className="hs-partner-firm-btn"
                  onClick={() => setPartnerMenuOpen(o => !o)}
                >
                  <span>{firmName || 'Advisor'}</span>
                  <span className="hs-partner-chevron">▾</span>
                </button>
                {partnerMenuOpen && (
                  <div className="hs-partner-menu">
                    {user?.email && (
                      <div className="hs-partner-menu-email">{user.email}</div>
                    )}
                    <button className="hs-signout" onClick={handleSignOut}>Sign out</button>
                  </div>
                )}
              </div>
            ) : (
              // ── Consumer controls ─────────────────────────────────────────
              <>
                <SubjectSwitcher />

                <div ref={consumerMenuRef} style={{ position: 'relative' }}>
                  <button
                    className="hs-partner-firm-btn"
                    onClick={() => setConsumerMenuOpen(o => !o)}
                  >
                    <span className="hs-email" style={{ display: 'inline' }}>
                      {user?.email}
                    </span>
                    <span className="hs-partner-chevron">▾</span>
                  </button>
                  {consumerMenuOpen && (
                    <div className="hs-partner-menu">
                      {user?.email && (
                        <div className="hs-partner-menu-email">{user.email}</div>
                      )}
                      <button className="hs-signout" onClick={handleSignOut}>Sign out</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Mobile nav strip */}
        {!onToolPage && (
          <div className="hs-mobile-nav">
            {navItems.map(item => (
              <HeaderLink key={item.to} to={item.to}>{item.label}</HeaderLink>
            ))}
          </div>
        )}
      </header>

      <div className={`${onToolPage ? 'hs-content' : 'hs-content-with-mobile-nav'}${showChrome ? '' : ' hs-content--no-header'}`}>
        {children}
      </div>

      {/* Always rendered (never conditionally omitted — see showChrome
          comment above); visibility toggled via class only. */}
      <footer className={`hs-footer${showChrome ? '' : ' hs-footer--hidden'}`}>
        <Link to="/terms"   className="hs-footer-link">Terms</Link>
        <span className="hs-footer-sep">·</span>
        <Link to="/privacy" className="hs-footer-link">Privacy</Link>
      </footer>
    </div>
  )
}